import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { Popup } from "components/Popup/Popup";
import { TextInput } from "../TextInput/TextInput";
import Button from "../Button/Button.jsx";
import { TypeButton } from "./TypeButton/TypeButton";

import popupStore from "components/Popup/PopupStore";
import paymentFormStore from "pages/PaymentForm/PaymentFormStore";

import { useMatchMedia } from "utils/hooks/use-match-media";

import "./PayModal.scss";

const AMOUNTS = {
  private: [500, 1000, 2000, 3000],
  business: [3000, 5000, 10000, 15000],
};

const MIN_BUSINESS_AMOUNT = 3000;
const MAX_PRIVATE_AMOUNT = 3000;

export const PayModal = observer(() => {
  const navigate = useNavigate();
  const { isMobile } = useMatchMedia();
  const [showMinAmountWarning, setShowMinAmountWarning] = useState(false);

  const isBusinessPerson = popupStore.isCheckboxChecked;
  const currentAmount = Number(popupStore.amount);
  const selectedAmounts = isBusinessPerson ? AMOUNTS.business : AMOUNTS.private;
  const isInForbiddenRange = currentAmount > MAX_PRIVATE_AMOUNT && currentAmount < MIN_BUSINESS_AMOUNT && popupStore.amount !== "";
  const isAmountBelowMinimum = isBusinessPerson && currentAmount < MIN_BUSINESS_AMOUNT && popupStore.amount !== "";
  const isPrivatePersonInvalid = !isBusinessPerson && currentAmount > MAX_PRIVATE_AMOUNT;
  const isPayButtonEnabled = isBusinessPerson 
    ? popupStore.isOkEnabled && currentAmount >= MIN_BUSINESS_AMOUNT 
    : popupStore.isOkEnabled && currentAmount > 0 && currentAmount <= MAX_PRIVATE_AMOUNT;

  useEffect(() => {
    if (isAmountBelowMinimum) {
      setShowMinAmountWarning(true);
      const timer = setTimeout(() => {
        setShowMinAmountWarning(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShowMinAmountWarning(false);
    }
  }, [isAmountBelowMinimum]);

  const handleSumButtonClick = (amount) => {
    popupStore.setAmount(amount.toString());
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    const regex = /^[1-9]?\d*$/;
    if (regex.test(value)) {
      popupStore.setAmount(value);
    }
  };

  const handlePersonTypeSelect = (isBusiness) => {
    popupStore.setIsCheckboxChecked(isBusiness);
  };

  const handlePayment = () => {
    if (isBusinessPerson) {
      paymentFormStore.resetForm(true);
      paymentFormStore.setAmountOfPayment(popupStore.amount);
      navigate("/payment-form");
      popupStore.close();
      popupStore.setIsCheckboxChecked(false);
    } else {
      popupStore.okPay();
    }
  };

  return (
    <Popup modalType="paymodal" title="Пополнение баланса">
      {!isBusinessPerson && !isMobile && (
        <p className="paymodal__limit-warning">
          Коллеги, мы временно ограничили на 15 дней оплату по Робокассе до 3 000 ₽<br />
          Если сумма пополнения более 3 000 ₽, то оплатить можно как: Юр. лицо / ИП.
        </p>
      )}

      <div className="paymodal__container">
        <p className="paymodal__text">Выберите способ пополнения</p>
        <div className="paymodal__choose">
          <TypeButton type="private" isSelected={!isBusinessPerson} onClick={() => handlePersonTypeSelect(false)} />
          <TypeButton type="business" isSelected={isBusinessPerson} onClick={() => handlePersonTypeSelect(true)} />
        </div>
      </div>

      <div className="paymodal__input-container">
        <p className="paymodal__text">Введите сумму перевода или выберите из списка</p>
        <TextInput
          className={isAmountBelowMinimum || isPrivatePersonInvalid || isInForbiddenRange ? "error-text" : ""}
          type="text"
          inputmode="numeric"
          placeholder="Сумма"
          maxLength={7}
          value={popupStore.amount}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === "0" && popupStore.amount === "") {
              e.preventDefault();
            }
          }}
          errortext={popupStore.amountErrorText}
        />
      </div>

      {isPrivatePersonInvalid && (
          <p className="paymodal__error-message">
            Для суммы более 3 000 ₽ выберите способ оплаты «Юр. лицо / ИП»"
            {!isMobile && <br /> } {" "}
            (временное ограничение на 15 дней) 
          </p>
        )}

      <div className="paymodal__buttons-container">
        {selectedAmounts.map((amount) => (
          <button className="paymodal__sum" key={amount} type="button" aria-label="Выберите сумму" onClick={() => handleSumButtonClick(amount)}>
            {amount.toLocaleString()} &#8381;
          </button>
        ))}
      </div>

      <div className="paymodal__buttons">
        <Button className="paymodal__button-cancel" title="Отмена" onClick={() => popupStore.close()} />
        <Button classes={["wide"]} title="Пополнить" onClick={handlePayment} disabled={!isPayButtonEnabled} />
      </div>

      <p className="paymodal__warning">* Если оплата по Робокассе не проходит, попробуйте указать другую почту для получения чека.</p>
      {showMinAmountWarning && (
        <div className="paymodal__min-amount-warning">
          <div>
            <p>Увеличьте сумму</p>
            <p>Счет для ИП и юр. лиц можно оформить при сумме от 3 000 ₽</p>
          </div>
          <button onClick={() => setShowMinAmountWarning(false)}>Ок</button>
        </div>
      )}

      {!isBusinessPerson && isMobile && (
        <p className="paymodal__limit-warning">
          Коллеги, мы временно ограничили на 15 дней оплату по Робокассе до 3 000 ₽<br />
          Если сумма пополнения более 3 000 ₽, то оплатить можно как: Юр. лицо / ИП.
        </p>
      )}
    </Popup>
  );
});
